import _ from 'underscore';
import {
    EventEmitter, Logger, memoizedDebounce, namespace,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractGroupItem from '../item/abstractGroupItem';
import ContainerItem from '../item/containerItem';
import ContainerItemInterface from '../item/containerItemInterface';
import Error from './problem/error';
import Warning from './problem/warning';

/**
 * @class ValidationWatcher
 * @extends EventEmitter
 */
export default class ValidationWatcher extends EventEmitter {
    /**
     * @type {Set.<AbstractItem>}
     */
    watchedItems = new Set();

    /**
     * @type {Map.<ValidatorInterface, AbstractProblem[]>}
     */
    problems = new Map();

    /**
     * @type {ValidatorInterface|null}
     */
    validator = null;

    /**
     * @param {ValidatorInterface|null} validator
     */
    constructor(validator) {
        super();
        this.validator = validator;

        this._triggerEvent = _.debounce(() => this.trigger('validate', this.getProblems()), 100);
    }

    /**
     * @param {ContainerItem} containerItem
     */
    watchContainerItem(containerItem) {
        Logger.debug('ValidationWatcher.watchContainerItem()', containerItem);

        if (this.watchedItems.has(containerItem)) return;

        ContainerItemInterface.assert(containerItem);

        this.watchedItems.add(containerItem);

        containerItem.on('change', memoizedDebounce(item => this._onContainerItemChange(item), 500), this);
        containerItem.getItems().on('add', item => this._onItemAdd(item), this);
        containerItem.getItems().on('change', memoizedDebounce(item => this._onItemChange(item), 500), this);
        containerItem.getItems().on('remove', item => this._onItemRemove(item), this);

        this._validateItem(containerItem);
        containerItem.getItems().each(item => this._validateItem(item));
    }

    unwatchContainerItem(containerItem) {
        Logger.debug('ValidationWatcher.unwatchContainerItem()', containerItem);

        if (!this.watchedItems.has(containerItem)) return;

        this.watchedItems.delete(containerItem);

        containerItem.off('change', null, this);
        containerItem.getItems().off('add', null, this);
        containerItem.getItems().off('change', null, this);
        containerItem.getItems().off('remove', null, this);
        this._onItemRemove(containerItem);
    }

    _onItemAdd(item) {
        this._validateItem(item);
    }

    _onContainerItemChange(changedItem) {
        this._validateItem(changedItem);
    }

    _onItemChange(changedItem) {
        // Check if this item will be handled via _onContainerItemChange
        if (this.watchedItems.has(changedItem)) return;

        // Check if this item has been deleted in the mean time
        if (!changedItem.getParentItem().getItems().contains(changedItem)) return;

        this._validateItem(changedItem);
    }

    _onItemRemove(item) {
        const items = item instanceof AbstractGroupItem ? item.resolveGroupItems() : [item];
        this._removeProblemsByItemRecursive(...items);
        this._triggerEvent();
    }

    /**
     * @param {AbstractItem} item
     */
    _validateItem(item) {
        this.validator
            ?.validateItem(item)
            .forEach(({ validator, items, problems }) => {
                if (!this.problems.has(validator)) this.problems.set(validator, []);
                this._removeProblemsByValidatorAndItem(validator, ...items);
                this.problems.get(validator).push(...problems);
            });

        Logger.debug('ValidationWatcher._validateItem()', item, this.problems);

        this._triggerEvent();
    }

    _problemContainsAnyGivenItem(problem, items) {
        return !!problem.getItems().find(subItem => items.indexOf(subItem) >= 0);
    }

    /**
     * @param {AbstractValidator} validator
     * @param {AbstractItem} item
     */
    _removeProblemsByValidatorAndItem(validator, ...items) {
        this.problems.set(
            validator,
            this.problems.get(validator).filter(problem => !this._problemContainsAnyGivenItem(problem, items)),
        );
    }

    /**
     * @param {AbstractValidator} validator
     * @param {AbstractItem} item
     */
    _removeProblemsByItem(...items) {
        this.problems.forEach((problems, validator) => this._removeProblemsByValidatorAndItem(validator, ...items));
    }

    /**
     * @param {AbstractItem} item
     */
    _removeProblemsByItemRecursive(...items) {
        const flatItems = items
            .map(item => (item instanceof ContainerItem ? [item].concat(item.getDescendants()) : [item]))
            .reduce((flattened, subItems) => flattened.concat(subItems), []);

        this._removeProblemsByItem(...flatItems);
    }

    _getProblemsOfKind(kind) {
        return this.getProblems()
            .filter(problem => problem instanceof kind)
            .reduce((flattened, problems) => flattened.concat(problems), []);
    }

    getProblems() {
        return [...this.problems]
            .reduce((flattened, [, problems]) => flattened.concat(problems), []);
    }

    getWarnings() {
        return this._getProblemsOfKind(Warning);
    }

    getErrors() {
        return this._getProblemsOfKind(Error);
    }

    isValid() {
        return this.getErrors().length === 0;
    }
}

namespace('Ofcs.Editor.Validator');
window.Ofcs.Editor.Validator.ValidationWatcher = ValidationWatcher;
